import * as Select from '@radix-ui/react-select';
import { AnimationEventHandler, useCallback, useState } from 'react';
import styled, { keyframes } from 'styled-components';

import { ArrowIcon } from '#/components/Accordion/Accordion.styled';
import { focusShadow } from '#/components/styles';
import {
  textBoxBaseStyle,
  TextBoxText,
  TextBoxTitle,
} from '#/pages/Settings/components/styled';
import { falsyToNull, remify } from '#/service/utils';

export interface TextBoxSelectItem {
  /** The value the item represents, typically an id. */
  value: string;
  /** The title to display for the item. */
  title: string;
}

export interface TextBoxSelectProps {
  /** Optional aria-label for accessibility. Falls back to `title`. */
  'aria-label'?: string;
  /** Array of items to choose from. Each item has a `value` (id) and a `title` to display. */
  items: TextBoxSelectItem[];
  /** The currently selected item's `value`. */
  selected: string | undefined;
  /** Callback to call when an item is selected. The parent component must update `selected`. */
  onSelect?: (value: string) => void;
  /** Optional title to display above the selected item. */
  title?: string;
  /** Optional placeholder to display when no item is selected. */
  placeholder?: string;
}

const TRANSITION = '150ms ease-out';

const openDown = keyframes`
  from { opacity: 0; transform: scaleY(0); }
  to { opacity: 1; transform: scaleY(1); }
`;

const closeUp = keyframes`
  from { opacity: 1; transform: scaleY(1); }
  to { opacity: 0; transform: scaleY(0); }
`;

const Container = styled.div`
  margin-bottom: ${remify(5)};
  user-select: none;

  .select-trigger {
    ${textBoxBaseStyle('', false)};
    color: inherit;
    border: none;
    margin: 0;
    width: 100%;
    overflow: hidden;
    outline: none;

    &:focus-visible {
      ${focusShadow};
    }
  }

  .select-icon {
    grid-area: button;
    font-size: 0;
    transition: rotate ${TRANSITION};
  }

  .select-separator {
    position: absolute;
    bottom: 0;
    height: 1px;
    width: 100%;
    background-color: var(--pageParagraphForeground);
    opacity: 0;
    transition: opacity ${TRANSITION};
  }

  .select-content {
    ${textBoxBaseStyle('', false)};
    max-height: var(--radix-select-content-available-height);
    width: var(--radix-select-trigger-width);
    overflow: hidden;
    place-items: unset;
    z-index: 1;
    border-radius: 0 0 ${remify(4)} ${remify(4)};
    transform-origin: center top;
    transform: scaleY(0);
    will-change: opacity, transform;
  }

  .select-content[data-state='open'] {
    animation: ${openDown} ${TRANSITION} forwards;
  }

  &.closing .select-content {
    animation: ${closeUp} ${TRANSITION} forwards;
  }

  .select-viewport {
    display: flex;
    flex-direction: column;
    gap: ${remify(8)};
    margin: ${remify(-8)};
    max-height: inherit;
    overflow: auto;
    overscroll-behavior: contain;
  }

  .select-item {
    outline: none;
    border-radius: ${remify(8)};
    padding: ${remify(8)};

    &:focus-visible {
      background: var(--pageBackground);
    }
  }

  &:not(.closing) .select-trigger[data-state='open'] {
    border-radius: ${remify(4)} ${remify(4)} 0 0;

    .select-icon {
      rotate: -180deg;
    }

    .select-separator {
      opacity: 0.1;
    }
  }
`;

export const TextBoxSelect = ({
  'aria-label': ariaLabel,
  items,
  selected,
  onSelect,
  title,
  placeholder,
}: TextBoxSelectProps) => {
  // maintain our own open/closing/closed state so we can animate the closing
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const onOpenChange = useCallback(() => {
    if (!isOpen) {
      setIsOpen(true);
      setIsClosing(false);
    } else if (!isClosing) {
      setIsClosing(true);
    }
  }, [isClosing, isOpen]);

  const onAnimationEnd: AnimationEventHandler = useCallback(
    (event) => {
      if (isClosing && event.target === event.currentTarget) {
        setIsClosing(false);
        setIsOpen(false);
      }
    },
    [isClosing]
  );

  const selectedTitle = items.find((item) => item.value === selected)?.title;
  const titleOrPlaceholder =
    falsyToNull(title) ?? (selected ? '' : placeholder);

  return (
    <Container
      className={`select-trigger-container ${isClosing ? 'closing' : ''}`}
    >
      <Select.Root
        onOpenChange={onOpenChange}
        onValueChange={onSelect}
        open={isOpen || isClosing}
        value={selected}
      >
        <Select.Trigger
          aria-label={ariaLabel ?? title}
          className="select-trigger"
        >
          {titleOrPlaceholder && (
            <TextBoxTitle>{titleOrPlaceholder}</TextBoxTitle>
          )}
          <Select.Value>
            {selectedTitle && (
              <TextBoxText data-testid="select-value">
                {selectedTitle}
              </TextBoxText>
            )}
          </Select.Value>
          <Select.Icon className="select-icon">
            <ArrowIcon name="chevron_down" />
          </Select.Icon>
          <div className="select-separator" />
        </Select.Trigger>
        <Select.Content
          aria-label={ariaLabel ?? title}
          avoidCollisions={false}
          className="select-content"
          onAnimationEnd={onAnimationEnd}
          position="popper"
          side="bottom"
        >
          <Select.Viewport className="select-viewport">
            {items.map(({ value, title }) => (
              <Select.Item className="select-item" key={value} value={value}>
                <Select.ItemText>{title}</Select.ItemText>
              </Select.Item>
            ))}
          </Select.Viewport>
        </Select.Content>
      </Select.Root>
    </Container>
  );
};
